import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Popper,
  styled,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material'
import lightTheme from '../../styles/theme/lightTheme'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPen,
  faFilePlus,
  faCalendarPlus,
  faExclamationTriangle,
  faComment,
  faSearch,
  faFile,
  faFileAlt,
  faCode,
  faUser,
  faHeart,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import lock from '../../assets/images/lock.png'
import '../../styles/components/_box.scss'
import { GradientButton } from '../buttons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { faInfoCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import { niveauCompetencesList } from '../../data'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  height: 435,
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  rowGap: '20px',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '&::before': {
      opacity: 1,
    },
  },
}))

const ItemLong = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '24px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
  },
}))

const BannerCircle = styled(Box)(({ theme }) => ({
  backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  columnGap: '30px',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',

  '.firstCircle, .secondCircle, .thirdCircle, .fourthCircle, .fiveCircle': {
    width: 10,
    height: 10,
    backgroundColor: lightTheme.palette.secondary.main,
    border: '1px solid #FFFFFF',
    borderRadius: '50%',
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 16,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  position: 'relative',
  paddingBottom: '8px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '2px',
  },
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const IconContainer = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  border: '2px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: 'rgba(102, 126, 234, 0.05)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    border: '2px solid rgba(102, 126, 234, 0.5)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}))

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  color: '#6366f1',
  fontWeight: 600,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  '&:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const LanguageChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(16, 185, 129, 0.08)',
  border: '1px solid rgba(16, 185, 129, 0.2)',
  color: '#10b981',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const SoftSkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.2)',
  color: '#f59e0b',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const CandidatureCard = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.2)',
  backgroundColor: 'rgba(102, 126, 234, 0.05)',
  marginBottom: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
  },
}))

const StatusBadge = styled(Box)(({ theme, status }) => ({
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  ...(status === 'postule' && {
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    color: '#ff9800',
  }),
  ...(status === 'accepte' && {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#4caf50',
  }),
  ...(status === 'refuse' && {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    color: '#f44336',
  }),
  ...(status === 'entretien' && {
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
    color: '#2196f3',
  }),
}))

const WrapperIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
}))

const InfoSummaryBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 16,
  borderRadius: 16,
  backgroundColor: 'rgba(244, 67, 54, 0.08)',
  border: '1px solid rgba(244, 67, 54, 0.18)',
  boxShadow: '0 10px 30px rgba(244, 67, 54, 0.08)',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

const MissingInfoCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 8,
  minHeight: 110,
  padding: '18px 16px',
  borderRadius: 14,
  border: '1px solid rgba(244, 67, 54, 0.16)',
  backgroundColor: '#fff7f7',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 16px 30px rgba(244, 67, 54, 0.12)',
  },
  '& .title': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  '& .label': {
    fontWeight: 700,
    color: '#b91c1c',
  },
  '& .description': {
    color: '#6b7280',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
  },
}))

const BannerSquare = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  columnGap: '30px',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',

  '.firstSquare, .secondSquare, .thirdSquare, .fourthSquare, .fiveSquare': {
    width: 10,
    height: 10,
    backgroundColor: '#FFFFFF',
    /*border: "1px solid #FFFFFF",
    borderRadius: "50%"*/
  },
}))

const UserMasonryLayout = ({
  user,
  userLangues,
  profil,
  besoinClient,
  resumeClient,
  besoin,
  resume,
  rendezVous,
  offre,
  files,
  atelier,
  ged,
  relation,
  valeurs,
  competences,
  competencesAnnexes,
  softskils,
  candidatures,
  type,
  displayValeurSection,
  displayCompetencesSection,
  displaySoftskilSection,
  handleOpen,
  handleOpenCalendar,
  handleOpenDoc,
  handleOpenBesoin,
  handleOpenResume,
  handleOpenValeurs,
  handleOpenCompetences,
  handleOpenCompetencesAnnexes,
  handleOpenSoftskils,
  handleOpenOffer,
  handleOpenAddEvent,
  loading,
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const [anchorEl2, setAnchorEl2] = useState(null)
  const [anchorEl3, setAnchorEl3] = useState(null)
  const [openTitle, setOpenTitle] = useState(false)
  const [openBesoin, setOpenBesoin] = useState(false)
  const [openResume, setOpenResume] = useState(false)
  const [showErrorDetails, setShowErrorDetails] = useState(true)

  const missingCv =
    files.filter((obj) => obj.type === 'Cv' || obj.type === 'cv').length === 0
  const missingLangues = userLangues.length === 0
  const missingBesoin = !besoin
  const missingResume = !resume
  const missingCompetences = competences.length === 0
  const missingSoftskills = softskils.length < 6
  const missingValues = valeurs.length < 6

  const missingItems = [
    missingLangues && {
      key: 'languages',
      icon: faComment,
      title: t('filter.languages'),
      subtitle: t('profile.addLanguagesMessage'),
      onClick: null,
    },
    missingBesoin && {
      key: 'besoin',
      icon: faSearch,
      title: t('profile.candidateSearch'),
      subtitle: t('profile.addJobSearchMessage'),
      onClick: handleOpenBesoin,
    },
    missingCv && {
      key: 'cv',
      icon: faFile,
      title: `${t('profile.ged')} (CV)`,
      subtitle: t('profile.addCvMessage'),
      onClick: handleOpenDoc,
    },
    missingResume && {
      key: 'resume',
      icon: faFileAlt,
      title: t('profile.candidateSummary'),
      subtitle: t('profile.addNewSummaryMessage'),
      onClick: handleOpenResume,
    },
    missingCompetences && {
      key: 'competences',
      icon: faCode,
      title: t('filter.competences'),
      subtitle: t('profile.addNewCompetencesMessage'),
      onClick: handleOpenCompetences,
    },
    missingSoftskills && {
      key: 'softskils',
      icon: faUser,
      title: t('filter.softskils'),
      subtitle: t('profile.addSoftskilsMessage'),
      onClick: handleOpenSoftskils,
    },
    missingValues && {
      key: 'valeurs',
      icon: faHeart,
      title: t('filter.values'),
      subtitle: t('profile.addNewValuesMessage'),
      onClick: handleOpenValeurs,
    },
  ].filter(Boolean)

  const totalErrors = missingItems.length

  const handleClickTitle = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
    setOpenTitle(!openTitle)
  }
  const handleClickBesoin = (event) => {
    setAnchorEl2(anchorEl2 ? null : event.currentTarget)
    setOpenBesoin(!openBesoin)
  }
  const handleClickResume = (event) => {
    setAnchorEl3(anchorEl3 ? null : event.currentTarget)
    setOpenResume(!openResume)
  }

  const sizeChip = useMediaQuery(lightTheme.breakpoints.down('sm')) ? 'small' : 'medium'

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  return (
    <Container
      maxWidth={'xl'}
      sx={{
        paddingTop: { xs: '16px', sm: '25px' },
        paddingBottom: { xs: '16px', sm: '25px' },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
        {(!besoin ||
          !resume ||
          userLangues.length === 0 ||
          competences.length === 0 ||
          /* competencesAnnexes.length < 6 || */
          softskils.length < 6 ||
          valeurs.length < 6 ||
          files.filter((obj) => obj.type === 'Cv' || obj.type === 'cv')
            .length === 0) && (
            <Grid item xs={12} order={{ sm: 1, lg: 1 }}>
              <Item
                sx={{
                  height: '100%',
                  border: '2px dashed rgba(244, 67, 54, 0.3)',
                  background:
                    'linear-gradient(135deg, rgba(244, 67, 54, 0.02) 0%, rgba(244, 67, 54, 0.05) 100%)',
                  position: 'relative',
                  overflow: 'hidden',
                  padding: { xs: 1, lg: 3 },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background:
                      'linear-gradient(90deg, #f44336 0%, #ff9800 100%)',
                  }}
                />
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      mb: 2,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      fontSize={20}
                      color={lightTheme.palette.error.main}
                    />
                    <Typography
                      variant="h6"
                      color={lightTheme.palette.error.main}
                      sx={{ fontWeight: 'bold' }}
                    >
                      {t('profile.candidateErrorInfo')}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 1.5, sm: 2 },
                    mb: 3,
                  }}
                >
                  {userLangues.length === 0 && (
                    <Box
                      onClick={() => { }}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 1.5 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        border: '1px solid rgba(244, 67, 54, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          borderColor: 'rgba(244, 67, 54, 0.2)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faComment}
                        fontSize={16}
                        color={lightTheme.palette.error.main}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color={lightTheme.palette.error.main}
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          {t('filter.languages')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={lightTheme.palette.error.main}
                        >
                          {t('profile.addLanguagesMessage')}
                        </Typography>
                      </Box>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        fontSize={12}
                        color={lightTheme.palette.error.main}
                        sx={{
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </Box>
                  )}

                  {!besoin && (
                    <Box
                      onClick={handleOpenBesoin}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 1.5 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        border: '1px solid rgba(244, 67, 54, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          borderColor: 'rgba(244, 67, 54, 0.2)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faSearch}
                        fontSize={16}
                        color={lightTheme.palette.error.main}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color={lightTheme.palette.error.main}
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          {t('profile.candidateSearch')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={lightTheme.palette.error.main}
                        >
                          {t('profile.addJobSearchMessage')}
                        </Typography>
                      </Box>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        fontSize={12}
                        color={lightTheme.palette.error.main}
                        sx={{
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </Box>
                  )}

                  {files.filter((obj) => obj.type === 'Cv' || obj.type === 'cv')
                    .length === 0 && (
                      <Box
                        onClick={handleOpenDoc}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'stretch', sm: 'center' },
                          gap: { xs: 1.5, sm: 1.5 },
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: '8px',
                          backgroundColor: 'rgba(244, 67, 54, 0.05)',
                          border: '1px solid rgba(244, 67, 54, 0.1)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            transform: { xs: 'none', sm: 'translateX(4px)' },
                            borderColor: 'rgba(244, 67, 54, 0.2)',
                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                          },
                          '&:active': {
                            transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                          },
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faFile}
                          fontSize={16}
                          color={lightTheme.palette.error.main}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            color={lightTheme.palette.error.main}
                            sx={{ fontWeight: 'bold', mb: 0.5 }}
                          >
                            {t('profile.ged')} (CV)
                          </Typography>
                          <Typography
                            variant="caption"
                            color={lightTheme.palette.error.main}
                          >
                            {t('profile.addCvMessage')}
                          </Typography>
                        </Box>
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          fontSize={12}
                          color={lightTheme.palette.error.main}
                          sx={{
                            opacity: 0.7,
                            transition: 'opacity 0.3s ease',
                          }}
                        />
                      </Box>
                    )}

                  {!resume && (
                    <Box
                      onClick={handleOpenResume}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 1.5 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        border: '1px solid rgba(244, 67, 54, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          borderColor: 'rgba(244, 67, 54, 0.2)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        fontSize={16}
                        color={lightTheme.palette.error.main}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color={lightTheme.palette.error.main}
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          {t('profile.candidateSummary')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={lightTheme.palette.error.main}
                        >
                          {t('profile.addNewSummaryMessage')}
                        </Typography>
                      </Box>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        fontSize={12}
                        color={lightTheme.palette.error.main}
                        sx={{
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </Box>
                  )}

                  {competences.length < 1 && (
                    <Box
                      onClick={handleOpenCompetences}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 1.5 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        border: '1px solid rgba(244, 67, 54, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          borderColor: 'rgba(244, 67, 54, 0.2)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCode}
                        fontSize={16}
                        color={lightTheme.palette.error.main}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color={lightTheme.palette.error.main}
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          {t('filter.competences')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={lightTheme.palette.error.main}
                        >
                          {t('profile.addNewCompetencesMessage')}
                        </Typography>
                      </Box>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        fontSize={12}
                        color={lightTheme.palette.error.main}
                        sx={{
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </Box>
                  )}

                  {softskils.length < 6 && (
                    <Box
                      onClick={handleOpenSoftskils}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 1.5 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        border: '1px solid rgba(244, 67, 54, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          borderColor: 'rgba(244, 67, 54, 0.2)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUser}
                        fontSize={16}
                        color={lightTheme.palette.error.main}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color={lightTheme.palette.error.main}
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          {t('filter.softskils')} : {t('errorMessage.list')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={lightTheme.palette.error.main}
                        >
                          {t('profile.addSoftskilsMessage')}
                        </Typography>
                      </Box>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        fontSize={12}
                        color={lightTheme.palette.error.main}
                        sx={{
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </Box>
                  )}

                  {valeurs.length < 6 && (
                    <Box
                      onClick={handleOpenValeurs}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1.5, sm: 1.5 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        border: '1px solid rgba(244, 67, 54, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          transform: { xs: 'none', sm: 'translateX(4px)' },
                          borderColor: 'rgba(244, 67, 54, 0.2)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'scale(0.95)' },
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faHeart}
                        fontSize={16}
                        color={lightTheme.palette.error.main}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color={lightTheme.palette.error.main}
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          {t('filter.values')} : {t('errorMessage.list')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={lightTheme.palette.error.main}
                        >
                          {t('profile.addNewValuesMessage')}
                        </Typography>
                      </Box>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        fontSize={12}
                        color={lightTheme.palette.error.main}
                        sx={{
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('profile.completeProfileMessage') || 'Completez votre profil pour augmenter votre visibilité auprès des recruteurs'}
                </Typography>
                <GradientButton
                  handleClick={() => {}}
                  sx={{
                    background: 'linear-gradient(135deg, #f44336 0%, #ff9800 100%)',
                    color: 'white',
                    px: 3,
                    py: 1,
                    borderRadius: '25px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {t('profile.completeNow') || 'Compléter mon profil'}
                </GradientButton>
              </Box> */}
              </Item>
            </Grid>
          )}
        <Grid item xs={12} sm={6} lg={6} order={1}>
          <Item
            sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              padding: { xs: 0, lg: 3 },
            }}
          >
            {type === 'entreprise' ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '60px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
                  zIndex: 1,
                }}
              >
                <TitleBox style={{ color: '#fff' }}>
                  {t('profile.entreprise.title')}
                </TitleBox>
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '60px',
                  background:
                    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',
                  zIndex: 1,
                }}
              >
                <TitleBox style={{ color: '#fff' }}>
                  {t('profile.candidat.title')}{' '}
                  <FontAwesomeIcon
                    style={{ cursor: 'pointer' }}
                    icon={openTitle ? faTimesCircle : faInfoCircle}
                    onClick={handleClickTitle}
                    fontSize={20}
                  />
                  <div>
                    <Popper
                      open={openTitle}
                      anchorEl={anchorEl}
                      placement="bottom"
                    >
                      <Paper
                        sx={{
                          padding: '10px',
                          width: { xs: '280px', sm: '400px' },
                          textAlign: 'justify',
                          borderRadius: '8px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        }}
                      >
                        <Typography>
                          {t('profile.candidateTitleInfo')}
                        </Typography>
                      </Paper>
                    </Popper>
                  </div>
                </TitleBox>
              </Box>
            )}

            <Box
              sx={{
                pt: { xs: 0, lg: '60px' },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {profil}
            </Box>
          </Item>
        </Grid>

        <Grid item xs={12} sm={6} lg={6} order={2} position={'relative'}>
          <Item
            sx={{
              height: '100%',
            }}
            className={''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.myApplications')}</TitleBox>
            </TitleContainer>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {candidatures}
            </Box>
          </Item>
        </Grid>

        <Grid item xs={12} sm={6} lg={6} order={3} position={'relative'}>
          <Item
            sx={{
              height: '100%',
            }}
            className={''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.rdv')}</TitleBox>
              <IconContainer onClick={handleOpenAddEvent}>
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  fontSize={15}
                  color={lightTheme.palette.primary.b2}
                />
              </IconContainer>
            </TitleContainer>

            <Box>{rendezVous}</Box>
          </Item>
        </Grid>
        <Grid item xs={12} sm={6} lg={6} order={4} position={'relative'}>
          <Item
            sx={{
              height: { sm: 400, lg: 435 },
            }}
            className={ged ? '' : 'blurLayout'}
          >
            <TitleContainer>
              <TitleBox
                sx={{
                  color:
                    files.filter(
                      (obj) => obj.type === 'Cv' || obj.type === 'cv'
                    ).length === 0
                      ? lightTheme.palette.error.main
                      : 'initial',
                }}
              >
                {t('profile.ged')}
                {files.filter((obj) => obj.type === 'Cv' || obj.type === 'cv')
                  .length === 0 && '*'}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenDoc}>
                  <FontAwesomeIcon
                    icon={faFilePlus}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                overflow: 'auto',
              }}
            >
              {/* {files.filter((obj) => obj.type === 'Cv' || obj.type === 'cv').length === 0 ? (
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faFile}
                      fontSize={32}
                      color={lightTheme.palette.error.main}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {t('profile.noCv') || 'Aucun CV'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('profile.addCvMessage') || 'Ajoutez votre CV pour augmenter votre visibilité'}
                  </Typography>
                  <GradientButton
                    handleClick={handleOpenDoc}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {t('profile.addCv') || 'Ajouter mon CV'}
                  </GradientButton>
                </Box>
              ) : (
                <></>
              )} */}

              <Box overflow={'auto'} width={'100%'} height={'100%'}>
                {ged}
              </Box>
            </Box>
          </Item>
          {!ged && (
            <img
              src={lock}
              alt={'Jobertech - not access'}
              style={{
                width: 150,
                height: 150,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-40%, -45%)',
              }}
            />
          )}
        </Grid>
        {/* <Grid
          item
          xs={12}
          sm={6}
          lg={4}
          order={{ sm: 4, lg: 5 }}
          position={'relative'}
        >
          <Item
            sx={{
              height: { sm: 400, lg: 435 },
            }}
            className={''}
          >
            <TitleBox>{t('profile.linking')}</TitleBox>

            <Box>{relation}</Box>
          </Item>
        </Grid> */}

        <Grid item xs={12} sm={6} order={5} position={'relative'}>
          <Item
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              rowGap: '20px',
              justifyContent: 'space-between',
            }}
            className={offre ? '' : ''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.purchaseHistory')}</TitleBox>
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {offre}
            </Box>

            <GradientButton displayIcon={'none'} onClick={handleOpenOffer}>
              {t('profile.addOffer')}
            </GradientButton>
          </Item>
        </Grid>

        <Grid item xs={12} sm={6} order={6} position={'relative'}>
          <Item
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              rowGap: '20px',
              justifyContent: 'space-between',
            }}
            className={atelier ? '' : ''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.atelierHistory')}</TitleBox>
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {atelier}
            </Box>

            <GradientButton
              displayIcon={'none'}
              onClick={() => {
                navigate('/ateliers')
              }}
            >
              {t('profile.addAtelier')}
            </GradientButton>
          </Item>
        </Grid>

        <Grid item xs={12} sm={6} lg={6} order={7}>
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox
                sx={{
                  color: !besoin ? lightTheme.palette.error.main : 'initial',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {t('profile.candidateSearch')}
                {!besoin && (
                  <Box
                    component="span"
                    sx={{
                      color: lightTheme.palette.error.main,
                      fontSize: '18px',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  >
                    *
                  </Box>
                )}
                <FontAwesomeIcon
                  style={{
                    cursor: 'pointer',
                    color: !besoin ? lightTheme.palette.error.main : 'inherit',
                  }}
                  icon={openBesoin ? faTimesCircle : faInfoCircle}
                  onClick={handleClickBesoin}
                  fontSize={18}
                />
                <div>
                  <Popper
                    open={openBesoin}
                    anchorEl={anchorEl2}
                    placement="bottom"
                  >
                    <Paper
                      sx={{
                        padding: '12px',
                        width: { xs: '280px', sm: '400px' },
                        textAlign: 'justify',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t('profile.candidateSearchInfo')}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile.candidateSearchDetails') ||
                          'Décrivez le type de poste que vous recherchez pour aider les recruteurs à vous trouver.'}
                      </Typography>
                    </Paper>
                  </Popper>
                </div>
              </TitleBox>
              <WrapperIcons>
                <IconContainer
                  onClick={handleOpenBesoin}
                  sx={{
                    backgroundColor: !besoin
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(102, 126, 234, 0.05)',
                    borderColor: !besoin
                      ? 'rgba(244, 67, 54, 0.3)'
                      : 'rgba(102, 126, 234, 0.3)',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={
                      !besoin
                        ? lightTheme.palette.error.main
                        : lightTheme.palette.primary.b2
                    }
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {!besoin ? (
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      fontSize={32}
                      color={lightTheme.palette.error.main}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {t('profile.searchIncomplete') || 'Recherche non définie'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t('profile.searchIncompleteMessage') ||
                      'Ajoutez votre recherche pour augmenter votre visibilité'}
                  </Typography>
                  <GradientButton
                    onClick={handleOpenBesoin}
                    sx={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {t('profile.addSearch') || 'Ajouter ma recherche'}
                  </GradientButton>
                </Box>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(102, 126, 234, 0.05)',
                      border: '1px solid rgba(102, 126, 234, 0.1)',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {besoinClient}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
                  >
                    <GradientButton
                      onClick={handleOpenBesoin}
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(102, 126, 234, 0.3)',
                        color: lightTheme.palette.primary.b2,
                        px: 2,
                        py: 0.5,
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontSize: '14px',
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.05)',
                        },
                      }}
                    >
                      {t('profile.modifySearch') || 'Modifier'}
                    </GradientButton>
                  </Box>
                </Box>
              )}
            </Box>
          </Item>
        </Grid>
        <Grid item xs={12} sm={6} order={8}>
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox
                sx={{
                  color: !resume ? lightTheme.palette.error.main : 'initial',
                }}
              >
                {t('profile.candidateSummary')}
                {!resume && '*'}{' '}
                <FontAwesomeIcon
                  style={{ cursor: 'pointer' }}
                  icon={openResume ? faTimesCircle : faInfoCircle}
                  onClick={handleClickResume}
                  fontSize={20}
                />
                <div>
                  <Popper
                    open={openResume}
                    anchorEl={anchorEl3}
                    placement="bottom"
                  >
                    <Paper
                      sx={{
                        padding: '10px',
                        width: '400px',
                        textAlign: 'justify',
                      }}
                    >
                      <Typography>
                        {t('profile.candidateSummaryInfo')}
                      </Typography>
                    </Paper>
                  </Popper>
                </div>
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenResume}>
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {!resume ? (
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      fontSize={32}
                      color={lightTheme.palette.error.main}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {t('profile.noSummary') || 'Résumé non défini'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t('profile.addSummaryMessage') ||
                      'Ajoutez votre résumé pour présenter votre profil'}
                  </Typography>
                  <GradientButton
                    onClick={handleOpenResume}
                    sx={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {t('profile.addSummary') || 'Ajouter mon résumé'}
                  </GradientButton>
                </Box>
              ) : (
                <Box>{resumeClient}</Box>
              )}
            </Box>
          </Item>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          order={9}
          display={displayCompetencesSection}
        >
          <ItemLong>
            <TitleContainer>
              <TitleBox
                sx={{
                  color:
                    competences.length < 1
                      ? lightTheme.palette.error.main
                      : 'initial',
                }}
              >
                {t('filter.competences')}
                {competences.length < 1 && '*'}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenCompetences}>
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {competences.length < 1 ? (
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCode}
                      fontSize={32}
                      color={lightTheme.palette.error.main}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {t('profile.noCompetences') || 'Aucune compétence'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t('profile.addCompetencesMessage') ||
                      'Ajoutez vos compétences pour valoriser votre profil'}
                  </Typography>
                  <GradientButton
                    onClick={handleOpenCompetences}
                    sx={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {t('profile.addCompetences') || 'Ajouter mes compétences'}
                  </GradientButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {competences &&
                    competences.map((competence, i) => (
                      <Chip
                        key={i}
                        size={sizeChip}
                        label={`${competence.competence} - ${niveauCompetencesList[competence.niveau - 1].titre[
                          i18n.language
                        ]
                          }`}
                        sx={{
                          margin: '4px',
                          backgroundColor: 'rgba(99, 102, 241, 0.08)',
                          border: '1px solid rgba(99, 102, 241, 0.2)',
                          color: '#6366f1',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          letterSpacing: '0.5px',
                        }}
                      />
                    ))}
                </Box>
              )}
            </Box>
          </ItemLong>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          order={10}
          display={displayCompetencesSection}
        >
          <ItemLong>
            <TitleContainer>
              <TitleBox
              /* sx={{
                  color:
                    competencesAnnexes.length < 6
                      ? lightTheme.palette.error.main
                      : 'initial',
                }} */
              >
                {t('filter.competencesAnnexes')}
                {/* {competencesAnnexes.length < 6 && '*'} */}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenCompetencesAnnexes}>
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {competencesAnnexes &&
                competencesAnnexes.map((competence, i) => (
                  <SkillChip
                    key={i}
                    label={`${competence.competence} - ${niveauCompetencesList[competence.niveau - 1].titre[
                      i18n.language
                    ]
                      }`}
                    size={sizeChip}
                    sx={{
                      backgroundColor: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      color: '#f59e0b',
                      '&:hover': {
                        backgroundColor: 'rgba(245, 158, 11, 0.12)',
                      },
                    }}
                  />
                ))}
            </Box>
          </ItemLong>
        </Grid>
        <Grid item xs={12} sm={12} order={11} display={displaySoftskilSection}>
          <ItemLong>
            <TitleContainer>
              <TitleBox
                sx={{
                  color:
                    softskils.length < 6
                      ? lightTheme.palette.error.main
                      : 'initial',
                }}
              >
                {t('filter.softskils')}
                {softskils.length < 6 && '*'}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenSoftskils}>
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {softskils &&
                softskils.map((softskil, i) => (
                  <SoftSkillChip key={i} label={softskil} size={sizeChip} />
                ))}
            </Box>
          </ItemLong>
        </Grid>
        <Grid item xs={12} sm={12} order={12} display={displayValeurSection}>
          <ItemLong>
            <TitleContainer>
              <TitleBox
                sx={{
                  color:
                    valeurs.length < 6
                      ? lightTheme.palette.error.main
                      : 'initial',
                }}
              >
                {t('filter.values')}
                {valeurs.length < 6 && '*'}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenValeurs}>
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {valeurs &&
                valeurs.map((valeur, i) => (
                  <ValueChip key={i} label={valeur} size={sizeChip} />
                ))}
            </Box>
          </ItemLong>
        </Grid>
      </Grid>
    </Container>
  )
}

export default UserMasonryLayout
